/**
 * AVBP Intake SCOPE resolver — Increment B (AD-030, 2026-06-22, Anil).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  THE OWNER RULING (2026-06-22): intake becomes FULLY PROJECT-SCOPED — the
 *  intake answers/stages/consent doc lives UNDER the project, not the client.
 *
 *  Today every intake reader/writer keys `clients/{C#####}/intake/{tier}`
 *  (intake.ts, intakeStages.ts, staffIntake.ts, Intake.tsx). This module is the
 *  ONE place that decides WHERE an intake doc lives, so the path can never drift
 *  across the four files again.
 *
 *  ─── THE PATH (AD-030) ───
 *    • CANONICAL (new):  projects/{VP###}/intake/{tier}
 *    • LEGACY  (old):    clients/{C#####}/intake/{tier}
 *
 *  Project ids are the `VP###` series (projectStore.projectIdFromSeq) — NOT the
 *  `P#####` Property series (the brief's example mislabelled it). A Property is a
 *  separate entity (AD-013); an intake references a Property via answers.propertyId.
 *
 *  ─── DUAL-PATH STRATEGY (why not a hard cutover) ───
 *  The REPORT-ENGINE Cloud Function (functions repo, `loadInputs`/`resolveIntakeSnap`)
 *  reads `clients/{cid}/intake/{tier}` SERVER-SIDE. A hard move would break every
 *  report until that CF is migrated in a separate functions dispatch. So:
 *    • READ  — prefer the project-scoped doc; FALL BACK to the legacy client doc
 *              when the project doc is absent (backward-compatible read, brief B.2).
 *    • WRITE — write the project-scoped doc (canonical) AND MIRROR the same payload
 *              to the legacy client doc, so the unmigrated report CF keeps working.
 *              The mirror is dropped once the CF reads project-scope (a one-line
 *              flip of WRITE_LEGACY_MIRROR + a functions dispatch).
 *  When NO project id is known (rare — a pre-project standalone intake, or the
 *  universal Scoping form before a project exists), the scope degrades to
 *  client-only — identical to today's behaviour, zero regression.
 *
 *  Firebase-free + React-free (pure) so the `.cjs` Node test runner exercises
 *  path + fallback resolution. The impure ref/load/write helpers live in
 *  `intakeScopeDb.ts` (imports `db`).
 * ─────────────────────────────────────────────────────────────────────────
 */

/**
 * An intake scope = the client it belongs to (always known) + the project it is
 * filed under (known for the project-scoped path; absent for a pre-project /
 * standalone intake). `clientId` is REQUIRED — persons are client-scoped
 * (AD-008/AD-019) and the legacy mirror needs it.
 */
export interface IntakeScope {
  /** The owning client (C#####). Always present. */
  clientId: string;
  /** The owning project (VP###). Absent → client-only (legacy) scope. */
  projectId?: string;
}

/** A Firestore doc path as an ordered segment tuple (collection/id/...). */
export type DocPath = readonly string[];

/**
 * WRITE_LEGACY_MIRROR — while the report-engine CF still reads client-scope, every
 * project-scoped write also mirrors to the legacy client doc. Flip to `false`
 * ONLY in the same change-set as the functions dispatch that points the CF at
 * `projects/{pid}/intake/{tier}`. Until then it MUST stay true.
 */
export const WRITE_LEGACY_MIRROR = true as const;

/**
 * PURE — the CANONICAL intake doc path for a scope.
 *   • project known → projects/{pid}/intake/{tier}
 *   • no project    → clients/{cid}/intake/{tier}  (legacy/standalone)
 */
export function canonicalIntakePath(scope: IntakeScope, tier: string): DocPath {
  const pid = (scope.projectId || '').trim();
  if (pid) return ['projects', pid, 'intake', tier];
  return ['clients', scope.clientId, 'intake', tier];
}

/** PURE — the LEGACY (client-scoped) intake doc path. */
export function legacyIntakePath(scope: IntakeScope, tier: string): DocPath {
  return ['clients', scope.clientId, 'intake', tier];
}

/**
 * PURE — every path a READ should TRY, in order. With a project: project-scoped
 * first, then the legacy client doc (backward-compatible read). Without a
 * project: just the client doc. De-dupes when canonical == legacy.
 */
export function intakeReadPaths(scope: IntakeScope, tier: string): DocPath[] {
  const canon = canonicalIntakePath(scope, tier);
  const legacy = legacyIntakePath(scope, tier);
  if (samePath(canon, legacy)) return [canon];
  return [canon, legacy];
}

/**
 * PURE — every path a WRITE should target. Canonical always; the legacy mirror
 * additionally when (a) the mirror is enabled AND (b) the canonical path is NOT
 * already the legacy one (i.e. a project is scoped). De-dupes.
 */
export function intakeWritePaths(scope: IntakeScope, tier: string): DocPath[] {
  const canon = canonicalIntakePath(scope, tier);
  const legacy = legacyIntakePath(scope, tier);
  if (samePath(canon, legacy)) return [canon];
  return WRITE_LEGACY_MIRROR ? [canon, legacy] : [canon];
}

/** PURE — segment-wise path equality. */
export function samePath(a: DocPath, b: DocPath): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

/** PURE — a human "projects/VP012/intake/L1A_R1" string (for the "Writing to" line + logs). */
export function pathString(p: DocPath): string {
  return p.join('/');
}

/**
 * PURE — the human banner trail for the form header: Client → Project → Property.
 * Each part is optional; the UI shows only the known ones. Property is the
 * answers.propertyId when present (kept here so the banner builder is testable).
 */
export interface ScopeTrail {
  client: string;
  project?: string;
  property?: string;
}

export function scopeTrail(
  scope: IntakeScope,
  propertyId?: string,
): ScopeTrail {
  const trail: ScopeTrail = { client: scope.clientId };
  const pid = (scope.projectId || '').trim();
  if (pid) trail.project = pid;
  const prop = (propertyId || '').trim();
  if (prop) trail.property = prop;
  return trail;
}
